import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { usePlayer } from "@/lib/playerStore";
import { isIOSSafari, isPWA } from "@/lib/platform";
import { requestInstallPrompt } from "@/lib/installPromptBus";

/**
 * Nudges iOS Safari users (not yet running as a PWA) to install AskThePods
 * to their home screen so audio keeps playing in the background and the
 * lock-screen / Dynamic Island controls work. iOS Safari tabs pause audio
 * when the tab is backgrounded — only the installed PWA gets true
 * background playback. We surface this exactly once the first time the
 * user starts playback, and respect a 14-day dismissal cooldown.
 */
const STORAGE_KEY = "atp:bg-audio-hint-dismissed-at";
const COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000;

function recentlyDismissed(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const t = Number(raw);
    return Number.isFinite(t) && Date.now() - t < COOLDOWN_MS;
  } catch {
    return false;
  }
}

export default function BackgroundAudioHint() {
  const playing = usePlayer((s) => s.playing);
  const hasTrack = usePlayer((s) => !!s.current);
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    if (!playing || !hasTrack) return;
    if (!isIOSSafari()) return;
    if (isPWA()) return;
    if (recentlyDismissed()) return;

    firedRef.current = true;
    toast("Keep listening with the screen off", {
      description:
        "Add AskThePods to your Home Screen so audio keeps playing when you switch apps or lock your phone.",
      duration: 12000,
      action: {
        label: "Install",
        onClick: () => {
          try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* ignore */ }
          requestInstallPrompt("feature-blocked", { force: true });
        },
      },
      onDismiss: () => {
        try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* ignore */ }
      },
      onAutoClose: () => {
        try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* ignore */ }
      },
    });
  }, [playing, hasTrack]);

  return null;
}
